import {
  MEDIA_ASPECT_RATIOS,
  MEDIA_ASPECT_RATIO_MAP,
  MEDIA_LIMITS,
  PLATFORMS,
  aspectRatioDimensions,
  preferredMediaUrl,
  platformsRejectingMedia,
  type MediaAsset,
  type MediaAspectRatioId,
  type MediaKind,
  type MediaStatus,
  type PlatformId,
} from '@ai-media/shared';

/**
 * Pure media-library helpers for the composer, the picker and the library page.
 * Kept free of React/axios so they can be unit tested.
 */

export interface AspectOption {
  id: MediaAspectRatioId;
  label: string;
  width: number;
  height: number;
  hint: string;
}

export function aspectOptions(): AspectOption[] {
  return MEDIA_ASPECT_RATIOS.map((id) => {
    const { width, height } = aspectRatioDimensions(id);
    return {
      id,
      label: MEDIA_ASPECT_RATIO_MAP[id].label,
      width,
      height,
      hint: id === 'square' ? 'feed' : id === 'portrait' ? 'instagram' : id === 'story' ? 'reels · tiktok' : 'youtube · x',
    };
  });
}

export function isVideoAsset(asset: Pick<MediaAsset, 'kind'>): boolean {
  return asset.kind === 'video';
}

export function isBusyAsset(asset: Pick<MediaAsset, 'status'>): boolean {
  return asset.status === 'queued' || asset.status === 'processing';
}

/** An asset can be published/attached only once it has a playable URL. */
export function isReadyAsset(asset: Pick<MediaAsset, 'status' | 'url' | 'thumbnail_url'>): boolean {
  return asset.status === 'ready' && Boolean(preferredMediaUrl(asset));
}

/** Small image used by grid tiles. */
export function assetPreviewUrl(asset: Pick<MediaAsset, 'thumbnail_url' | 'url'>): string | null {
  return asset.thumbnail_url ?? asset.url ?? null;
}

export function assetPlayUrl(asset: Pick<MediaAsset, 'url' | 'thumbnail_url'>): string | null {
  return preferredMediaUrl(asset);
}

export function formatDuration(seconds: number | null | undefined): string {
  if (!seconds || seconds <= 0) return '0:00';
  const total = Math.round(seconds);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

export function describeAsset(asset: MediaAsset): string {
  const parts: string[] = [asset.kind];
  if (isVideoAsset(asset)) parts.push(formatDuration(asset.duration_seconds));
  if (asset.width && asset.height) parts.push(`${asset.width}×${asset.height}`);
  if (asset.degraded) parts.push('placeholder');
  return parts.join(' · ');
}

/** Platforms that would reject any of the selected assets. */
export function blockingPlatforms(assets: MediaAsset[], platforms: PlatformId[]): PlatformId[] {
  const blocking = new Set<PlatformId>();
  for (const asset of assets) {
    for (const platform of platformsRejectingMedia(platforms, asset.kind)) {
      blocking.add(platform);
    }
  }
  return [...blocking];
}

export function blockingPlatformLabels(assets: MediaAsset[], platforms: PlatformId[]): string[] {
  return blockingPlatforms(assets, platforms).map((id) => PLATFORMS[id]?.label ?? id);
}

/** Currently selected assets in the order they were picked. */
export function selectedAssets(assets: MediaAsset[], ids: string[]): MediaAsset[] {
  const byId = new Map(assets.map((asset) => [asset.id, asset]));
  return ids.map((id) => byId.get(id)).filter((asset): asset is MediaAsset => Boolean(asset));
}

/** Add/remove an id while respecting the per-post media cap. */
export function toggleSelection(
  ids: string[],
  id: string,
  max = MEDIA_LIMITS.maxMediaPerPost
): { ids: string[]; added: boolean } {
  if (ids.includes(id)) {
    return { ids: ids.filter((value) => value !== id), added: false };
  }
  if (ids.length >= max) return { ids, added: false };
  return { ids: [...ids, id], added: true };
}

export function statusI18nKey(status: MediaStatus): string {
  return `media.status.${status}`;
}

export function kindI18nKey(kind: MediaKind): string {
  return `media.kind.${kind}`;
}

/** True while any asset is still rendering — the library page then polls. */
export function hasPendingAssets(assets: Pick<MediaAsset, 'status'>[]): boolean {
  return assets.some(isBusyAsset);
}
